import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  DatasourceInstructionMode,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { ArrowLeft, FileText } from 'lucide-react';

import { CSVImportDigitalProductsButton } from '../csv-import-digital-products-button';
import { Button } from '../ui/button';
import { BaseImportViewProps } from './import-view-props';

interface ViewCouponsImportCsvProps extends BaseImportViewProps {
  dealId: string;
  downloadTemplate: () => void;
  setImportedCouponGtins: (gtins: string[]) => void;
  setFromCsvImport: (isFrom: boolean) => void;
  setPauseCouponRefetch: (pause: boolean) => void;
  products?: DealItemWithVersions[];
}

export const ViewCouponsImportCsv: React.FC<ViewCouponsImportCsvProps> = ({
  setImportView,
  dealId,
  downloadTemplate,
  setImportedCouponGtins,
  setFromCsvImport,
  setPauseCouponRefetch,
  products = [],
}) => {
  const { t } = useTranslation();
  const hasProducts = products.length > 0;
  const [selectedMode, setSelectedMode] = useState<DatasourceInstructionMode>(
    DatasourceInstructionMode.CreateOnly
  );

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={() => setImportView('coupons')}
            aria-label="Revenir aux options coupons"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>
            {t('components.import_flow.coupons_import_csv_title')}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.coupons_import_csv_message')}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-6 py-6">
        <RadioGroup
          value={selectedMode}
          onValueChange={(value) =>
            setSelectedMode(value as DatasourceInstructionMode)
          }
          className="space-y-4"
        >
          <div className="flex items-start space-x-3">
            <RadioGroupItem
              value={DatasourceInstructionMode.CreateOnly}
              id="digital-create-only"
              className="mt-1"
            />
            <div className="space-y-1">
              <Label
                htmlFor="digital-create-only"
                className="cursor-pointer text-base font-medium"
              >
                {t(
                  'components.import_flow.digital_products_option_selection.add_new_digital_products'
                )}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t(
                  'components.import_flow.digital_products_option_selection.add_new_digital_products_description'
                )}
              </p>
            </div>
          </div>

          {hasProducts && (
            <div className="flex items-start space-x-3">
              <RadioGroupItem
                value={DatasourceInstructionMode.UpdateOnly}
                id="digital-update-only"
                className="mt-1"
              />
              <div className="space-y-1">
                <Label
                  htmlFor="digital-update-only"
                  className="cursor-pointer text-base font-medium"
                >
                  {t(
                    'components.import_flow.digital_products_option_selection.update_existing_digital_products'
                  )}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'components.import_flow.digital_products_option_selection.update_existing_digital_products_description'
                  )}
                </p>
              </div>
            </div>
          )}
        </RadioGroup>

        <Button
          className="w-full justify-start gap-2 border border-gray-300 bg-white text-black hover:bg-gray-50"
          onClick={downloadTemplate}
        >
          <FileText className="h-4 w-4" />
          <span className="text-sm">{t('general.download_csv_model')}</span>
        </Button>
        <CSVImportDigitalProductsButton
          dealId={dealId}
          buttonText={t('components.import_flow.coupons_import_csv_title')}
          pauseRefetch
          instructionMode={selectedMode}
          onUploadSuccess={async (gtins) => {
            setImportedCouponGtins(gtins ?? []);
            setFromCsvImport(true);
            setPauseCouponRefetch(true);
            setImportView('couponsCGU');
          }}
        />
      </div>
    </>
  );
};
